import { useEffect, useState } from "react";
import {
  BUTTON_CONNECT_ACCOUNT,
  BUTTON_CONNECT_TIKTOK,
} from "../../i18n/keysTranslations";
import {
  BUTTON_TIKTOK_BUSINESS_ACTION_ID,
  IS_WHITE_LABEL_BRAND,
  MODE_BACK_DROP_CONNECT,
  TIKTOK,
  TIKTOK_BUSINESS,
} from "../../utils/constants";
import Avatar from "@mui/material/Avatar";
import { logoPlatform } from "../../utils/string";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  startConnectTiktokBusiness,
  startGetTokenTiktokBusiness,
} from "../../actions/user";
import { startAfterPlatformConnected } from "../../actions/agencyAccountLinking";
import { showPlatformConnectedAlert } from "../../utils/showPlatformConnectedAlert";
import IconButton from "./IconButton";
import Loader from "../Loaders/Loader";
import AddIcon from "@mui/icons-material/Add";
import { ConfirmAlert } from "../Alerts/Alerts";
import BackDropLoader from "../Loaders/BackDropLoader";
// import { getNameBusinessTheme } from "../../actions/getters";
import ConnectionProgressModal from "../Containers/ConnectionProgressModal";
import { createPortal } from "react-dom";
import Button from "./Button";
import ModalTikTokConnectOrCreateAccount from "../Modal/ModalTikTokConnectOrCreateAccount";
import ModalCreateAdAccountTikTokBusiness from "../Modal/ModalCreateAdAccountTikTokBusiness";
import { TIKTOK_ACCOUNT_ACTION_STEP } from "./tikTokAccountActionStep";

const ButtonTikTokBusinessAction = ({
  id = BUTTON_TIKTOK_BUSINESS_ACTION_ID,
  text = BUTTON_CONNECT_TIKTOK,
  prevBlockClick = false,
  onPrevBlockClick = () => {},
  onClick = () => {},
  onCallbackOpenPopup = () => {},
  disabled = false,
  sx = {},
  // scope = TIKTOK_BUSINESS_SCOPES,
  logo = TIKTOK_BUSINESS,
  showLogo = true,
  type = "button",
  mode = "verify",
  requirePlatformConnection = true,
  icon = <AddIcon />,
  disableRipple = false,
  showButtonIcon = false,
  doTranslation = true,
  offerCreateAdAccount = true,
  onCreateStepChange,
  connectAccountRef = null,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [openBackDrop, setOpenBackDrop] = useState(false);
  const [externalPopup, setExternalPopup] = useState(null);
  const [modeBackDrop, setModeBackDrop] = useState();
  const [uncontrolledCreateStep, setUncontrolledCreateStep] = useState(null);
  const isCreateStepControlled = typeof onCreateStepChange === "function";
  const createStep = isCreateStepControlled ? null : uncontrolledCreateStep;

  const setCreateStep = (nextStep) => {
    if (isCreateStepControlled) {
      onCreateStepChange(nextStep);
      return;
    }
    setUncontrolledCreateStep(nextStep);
  };

  const dispatch = useDispatchApp();
  const integrations = useSelectorApp((state) => state?.user?.integrations);
  const tikTokBusinessIntegrations = integrations?.tiktokBusiness || {};
  const isTikTokBusinessConnected = Object.values(
    tikTokBusinessIntegrations
  ).some((integration) => integration.isActive);
  const { t } = useTranslationApp();
  // const NAME_BUSINESS = dispatch(getNameBusinessTheme());

  const onConnectAccount = async () => {
    const result = await ConfirmAlert({
      icon: false,
      confirmButtonText: t(BUTTON_CONNECT_ACCOUNT),
      html: <ConnectionProgressModal platform={TIKTOK} />,
      width: "60vh",
    });

    if (!result?.isConfirmed) {
      return false;
    }

    setModeBackDrop(MODE_BACK_DROP_CONNECT.GET_POPUP);
    dispatch(
      startConnectTiktokBusiness({
        onChangeLoading: (loading) => {
          setIsLoading(loading);
          setOpenBackDrop(loading);
          if (!loading) {
            onCallbackOpenPopup();
          }
        },
        onChangePopupReference: (popup) => setExternalPopup(popup),
      })
    );
    return true;
  };

  if (connectAccountRef) {
    connectAccountRef.current = onConnectAccount;
  }

  // The intermediate modal (connect or create) shows by default everywhere.
  // Pass offerCreateAdAccount={false} to skip it. Connecting from it
  // (or from the Business Center selector of the create modal) goes straight to
  // the connection guide + popup, so the user never loops back to the modal.
  const onStartConnection = async () => {
    const shouldOffer =
      typeof offerCreateAdAccount === "function"
        ? offerCreateAdAccount()
        : offerCreateAdAccount;
    if (shouldOffer && !IS_WHITE_LABEL_BRAND) {
      setCreateStep(TIKTOK_ACCOUNT_ACTION_STEP.CONNECT_OR_CREATE);
      return;
    }
    await onConnectAccount();
  };

  const onCloseCreateStep = () => setCreateStep(null);

  const onPressAction = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (prevBlockClick) {
      onPrevBlockClick();
      return;
    }

    if (
      requirePlatformConnection &&
      ((!isTikTokBusinessConnected && mode === "verify") || mode === "add")
    ) {
      await onStartConnection();
      return;
    }

    setIsLoading(true);
    await onClick();
    setIsLoading(false);
  };

  const onGetTokenTiktokBusiness = async ({ authCode, state }) => {
    setModeBackDrop(MODE_BACK_DROP_CONNECT.GET_INFORMATION);
    setOpenBackDrop(true);
    const response = await dispatch(
      startGetTokenTiktokBusiness({ authCode, state, withAuth: true })
    );
    setOpenBackDrop(false);

    if (response.ok) {
      await showPlatformConnectedAlert({ t });
      await dispatch(
        startAfterPlatformConnected({
          platform: TIKTOK_BUSINESS,
          integration: response.data,
        })
      );
      await onClick();
    }
  };

  useEffect(() => {
    if (!externalPopup) {
      return;
    }

    const timer = setInterval(() => {
      if (!externalPopup || externalPopup.closed) {
        clearInterval(timer);
        setExternalPopup(null);
        return;
      }

      try {
        const currentUrl = externalPopup.location.href;
        if (!currentUrl) {
          return;
        }
        const searchParams = new URL(currentUrl).searchParams;
        const authCode = searchParams.get("auth_code");
        const state = searchParams.get("state");

        if (authCode && state) {
          externalPopup.close();
          setExternalPopup(null);
          clearInterval(timer);

          onGetTokenTiktokBusiness({
            authCode,
            state,
          });
        }
      } catch {
        if (externalPopup.closed) {
          clearInterval(timer);
          setExternalPopup(null);
        }
      }
    }, 500);

    return () => {
      timer && clearInterval(timer);
    };
    // eslint-disable-next-line
  }, [externalPopup]);

  const createAdAccountModals = !isCreateStepControlled && (
    <>
      {createStep === TIKTOK_ACCOUNT_ACTION_STEP.CONNECT_OR_CREATE && (
        <ModalTikTokConnectOrCreateAccount
          modalOpen
          onCloseModal={onCloseCreateStep}
          onConnectAccount={onConnectAccount}
          onCreateAccount={() =>
            setCreateStep(TIKTOK_ACCOUNT_ACTION_STEP.CREATE_AD_ACCOUNT)
          }
        />
      )}
      {createStep === TIKTOK_ACCOUNT_ACTION_STEP.CREATE_AD_ACCOUNT && (
        <ModalCreateAdAccountTikTokBusiness
          modalOpen
          integrationIDs={Object.keys(tikTokBusinessIntegrations)}
          onCloseModal={onCloseCreateStep}
          onConnectAccount={onConnectAccount}
        />
      )}
    </>
  );

  return (
    <>
      {openBackDrop &&
        createPortal(
          <BackDropLoader
            open={openBackDrop}
            platform={TIKTOK_BUSINESS}
            from={modeBackDrop}
          />,
          document.body
        )}
      {createAdAccountModals}
      {type === "icon" ? (
        <IconButton
          id={id}
          disableRipple={disableRipple}
          sx={{ ...sx }}
          onClick={onPressAction}
        >
          {isLoading ? <Loader hasMarginTop={false} size={20} /> : icon}
        </IconButton>
      ) : (
        <Button
          id={id}
          disabled={disabled}
          loading={isLoading}
          color="secondary"
          sx={{
            ...sx,
          }}
          onClick={onPressAction}
        >
          {showButtonIcon && icon}
          {showLogo && (
            <Avatar
              src={logoPlatform(logo)}
              variant="square"
              sx={{
                width: "20px",
                height: "20px",
                mr: 1,
                ".MuiAvatar-img": {
                  objectFit: "contain",
                },
              }}
            />
          )}
          {doTranslation ? t(text) : text}
        </Button>
      )}
    </>
  );
};

export default ButtonTikTokBusinessAction;
